/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getSelectedProductIdFromActionInput, getDimensionValuesFromQuery } from '@msdyn365-commerce-modules/retail-actions';
import { ProductDimension, retailAction } from '@msdyn365-commerce/retail-proxy';
import { IGetProductPromotionResponse } from './DataServiceEntities.g';
import { getProductPromotionsV2Input } from './DataActionExtension.g';
import { getProductVariantOrMaster } from './getProductVariantOrMaster';

/**
 * GetProductPromotions Input Action
 */
export class GetProductPromotionsInput implements Msdyn365.IActionInput {
    public readonly recordId: number;
    public readonly dimensionValues: ProductDimension[];

    constructor(recordId: number, dimensionValues: ProductDimension[]) {
        this.recordId = recordId;
        this.dimensionValues = dimensionValues;
    }

    public getCacheKey = () => `${this.recordId}-${this.dimensionValues.map(v => v.DimensionValue?.Value ?? '').join('|')}`;
    public getCacheObjectType = () => 'HEI-ProductPromotions';
    public dataCacheType = (): Msdyn365.CacheType => (process.env.NODE_ENV === 'development' ? 'application' : 'none');
}

const createInput = (context: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const matchingDimensionValues = getDimensionValuesFromQuery(context.requestContext.url.requestUrl);
    const productId = getSelectedProductIdFromActionInput(context);
    if (productId) {
        return new GetProductPromotionsInput(+productId, matchingDimensionValues);
    } else {
        throw new Error('Unable to create GetProductPromotionsInput, no productId found on module config or query');
    }
};

export async function getProductPromotionsAction(
    input: GetProductPromotionsInput,
    context: Msdyn365.IActionContext
): Promise<IGetProductPromotionResponse> {
    const matchingDimensionValues = input.dimensionValues;
    process.env.NODE_ENV === 'development' && console.log('get-product-promotions.action', input.recordId, matchingDimensionValues);
    const productVariant = await getProductVariantOrMaster(input.recordId, matchingDimensionValues, context);

    const distinctProductVariantId = productVariant.RecordId;
    process.env.NODE_ENV === 'development' && console.log('get-product-promotions.action', input.recordId, distinctProductVariantId);
    const productPromotionsInput = getProductPromotionsV2Input(input.recordId, distinctProductVariantId);
    return retailAction(productPromotionsInput, context);
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetProductPromotionResponse>>getProductPromotionsAction,
    id: 'hei-get-product-promotions',
    input: createInput
    // isBatched: true
});
